/**
 * Wallet and balance service.
 *
 * Thin layer between the api helpers and the store. It maps the gateway's
 * snake_case response records onto the camelCase store shapes the views read,
 * drives the loading and error flags, and keeps balance reads network-only.
 * Views call these functions and then subscribe to the relevant store slice.
 */

import {
  getWallets as apiGetWallets,
  getWalletBalances as apiGetWalletBalances,
  getTransactions as apiGetTransactions,
  createWallet as apiCreateWallet,
  resolveImportedToken as apiResolveImportedToken,
  ApiError,
} from '@/lib/api';
import type { ImportedToken } from '@/lib/importedTokens';
import { getRefreshToken } from '@/lib/session';
import {
  setWallets,
  setWalletsLoading,
  setWalletsError,
  setWalletBalances,
  setBalancesLoading,
  setBalancesError,
  setActivity,
  appendActivity,
  setActivityLoading,
  setActivityError,
  upsertWallet,
} from '@/store/store';
import type {
  WalletSummary,
  WalletBalances,
  BalanceLine,
  BalanceLineNetwork,
  ActivityItem,
} from '@/lib/types';
import { chainKeyTier } from '@/lib/network';

/** Coerce an unknown gateway field to a string, or null when the field is absent. */
function strOrNull(v: unknown): string | null {
  return v == null ? null : String(v);
}

/** Coerce an unknown gateway field to a number, or null when the field is absent. */
function numOrNull(v: unknown): number | null {
  return v == null ? null : Number(v);
}

/**
 * Narrow an unknown gateway field to a usable address, or null.
 *
 * Deliberately NOT `strOrNull`: coercion turns an object into "[object Object]"
 * and a number into "42", both of which are non-empty strings that would pass a
 * plain emptiness check and reach a receive screen. Trimmed, because whitespace
 * is the same hazard with a friendlier face.
 *
 * @param v The raw gateway field.
 * @returns The address, or null when the field is not a usable one.
 */
function addressOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  // The TRIMMED value, not the original. Testing `trim()` and then returning `v`
  // lets "  bc1q…  " through padded, and it is then encoded into a QR payload
  // and copied to a clipboard with its whitespace intact.
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Narrow an unknown gateway field to a record, or an empty record when absent. */
function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

/**
 * Map a gateway wallet record to the store's WalletSummary shape. The gateway
 * response is untrusted, so every field is coerced rather than asserted.
 *
 * @param w Gateway WalletResponse record.
 * @returns The mapped summary.
 */
export function mapWallet(w: Record<string, unknown>): WalletSummary {
  return {
    id: String(w.id),
    label: String(w.label ?? ''),
    status: String(w.status ?? 'unknown'),
    curve: strOrNull(w.curve),
    chain: strOrNull(w.chain),
    publicKey: strOrNull(w.public_key),
    // NOT `strOrNull`. That coerces with String(), so a malformed field arrives
    // as "[object Object]" or "42" — non-empty strings that pass every
    // emptiness check and would be rendered as a RECEIVE ADDRESS. A user who
    // copies one loses whatever they send to it.
    //
    // Only a genuine, non-blank string is an address. Anything else is null,
    // which the readiness predicate already treats as not-yet-usable.
    //
    // The gateway derives this from the public key on every read, so it is
    // populated as soon as a wallet is finalized, without waiting for a balances
    // read. It is null while the wallet has no public key yet.
    address: addressOrNull(w.address),
    // Carried because it is the ONLY signal that a wallet is frozen: `status`
    // stays "active" throughout. Dropping it, as an earlier version did, left
    // Send and Receive enabled on a wallet the signing path would refuse.
    frozenAt: strOrNull(w.frozen_at),
  };
}

/**
 * Whether a wallet can actually be used — shown with a balance, sent from,
 * received into.
 *
 * REQUIRES AN ADDRESS, NOT JUST A STATUS. A wallet row exists in the database
 * from the moment the engine anchors its shares, which is BEFORE the DKG
 * finishes and before any public key or address exists. Treating the record's
 * presence as readiness surfaces a wallet with a blank address that cannot
 * receive funds, and a receive screen showing an empty address is a way to lose
 * money — a user copies nothing, or copies the placeholder.
 *
 * Both conditions are checked rather than either: the status guards a wallet
 * that was frozen or is otherwise not active, and the address guards the window
 * where a row exists but the ceremony has not produced a key.
 *
 * @param w The wallet summary.
 * @returns True when the wallet is usable.
 */
export function walletLifecycleUsable(w: WalletSummary): boolean {
  // Active and not frozen — everything about the wallet EXCEPT whether this
  // particular record carries an address.
  //
  // Separated from `isWalletReady` because the address has two sources: the list
  // endpoint may not carry a derived one, in which case the balances view holds
  // it (see Receive.tsx). Gating a surface on `isWalletReady` alone therefore
  // hid a perfectly good view address whenever the summary's was null — a
  // supported state, and a regression this split exists to prevent.
  return w.status === 'active' && w.frozenAt === null;
}

export function isWalletReady(w: WalletSummary): boolean {
  if (!walletLifecycleUsable(w)) return false;
  // A REAL, TRIMMED STRING. The gateway records are coerced with String(), so a
  // malformed field arrives as "[object Object]" — non-empty, and it would be
  // rendered as a receive address. Whitespace fails for the same reason.
  return typeof w.address === 'string' && w.address.trim().length > 0;
}

/**
 * Whether the Send / Receive / Swap actions may be offered.
 *
 * Extracted from the view so it can be tested: this project's vitest
 * environment is node-only, so a decision left inside the component would be
 * covered by nothing.
 *
 * `all` is enabled because there is no single wallet to judge and the
 * destination views carry their own pickers. A concrete selection is enabled
 * only when that wallet is genuinely usable — see {@link isWalletReady} for why
 * an address, not merely a record, is the test.
 *
 * A selection that resolves to NO wallet is enabled rather than blocked: that
 * means the id did not match the active tier's list, which is a filtering state
 * rather than an unready wallet, and the destination view handles it. Failing
 * closed there would disable the actions for reasons unrelated to provisioning.
 *
 * @param selectedAccountId The active selection, or `all`.
 * @param selectedWallet The resolved wallet for that selection, when there is one.
 * @returns True when the actions may be enabled.
 */
/**
 * The wallets that may be offered as a SIGNING SOURCE.
 *
 * Send and Swap both spend from a wallet, so a wallet that cannot sign must not
 * appear in their pickers at all. Two cases reach that state routinely now:
 *
 *   mid-ceremony  a row exists from the moment shares are anchored, before any
 *                 key or address is derived — the ordinary state for a few
 *                 seconds after registration, since the setup screen leaves
 *                 after a fixed three seconds
 *   frozen        `status: "active"` plus a timestamp; the signing path refuses
 *                 every request against it
 *
 * Filtering here rather than blocking at submit is deliberate: a wallet the user
 * picked, filled in a destination and an amount for, and only then discovered
 * was unusable is a worse experience than one that was never offered.
 *
 * RECEIVE IS NOT FILTERED THIS WAY. It has no signing requirement, and it already
 * renders an explicit pending state for a wallet whose address has not landed —
 * which tells the user more than hiding the wallet would.
 *
 * @param list The wallets to filter.
 * @returns Those that can currently sign.
 */
export function signableWallets(list: WalletSummary[]): WalletSummary[] {
  return list.filter(isWalletReady);
}

export function walletActionsEnabled(
  selectedAccountId: string,
  selectedWallet: WalletSummary | null,
): boolean {
  if (selectedAccountId === 'all') return true;
  if (!selectedWallet) return true;
  return isWalletReady(selectedWallet);
}

/**
 * Whether a wallet is still being created.
 *
 * Distinguished from "not ready" because the two want different UI: a wallet
 * mid-ceremony gets a spinner and will resolve on its own, while a frozen or
 * failed one will not and must not be presented as if waiting will fix it.
 *
 * @param w The wallet summary.
 * @returns True when a ceremony is still expected to complete.
 */
export function isWalletProvisioning(w: WalletSummary): boolean {
  if (isWalletReady(w)) return false;
  // ONLY the gateway's own pre-DKG term.
  //
  // An earlier version also counted an `active` wallet with no address, on the
  // theory that the address simply had not landed yet. That was WRONG and it
  // masked a broken wallet: finalization writes the public key, chain and
  // `active` status together, and the address is DERIVED from the public key on
  // every read. So `active` without an address is not a transitional state — it
  // means the key or chain data is missing or unsupported, and no amount of
  // waiting fixes it. Showing a spinner there promises something that never
  // arrives.
  //
  // A frozen wallet is likewise not provisioning: waiting will not unfreeze it.
  return w.status === 'provisioning' && w.frozenAt === null;
}

/**
 * The wallet kinds the create UI offers. Each entry is one (curve, chain) pair
 * the gateway accepts, so the user picks a chain by name and never types a curve
 * or chain string. The ed25519 curve is shared by Solana and Sui, so the chain
 * key is what disambiguates them; the secp256k1 curve is shared by the unified
 * EVM account and Tron.
 *
 * Only Bitcoin relies on a null chain: its curve (schnorr-taproot-secp256k1) is
 * unique, so the gateway can resolve the concrete per-tier chain from the curve
 * alone. Tron's chain is also null in this catalog, but because it shares the
 * secp256k1 curve with EVM a null chain would resolve to EVM at the gateway, so
 * the create view overrides it with the tier-specific concrete key ('tron' on
 * mainnet, 'tron-nile' on testnet) at submit time. The gateway pins whichever
 * key it resolves immutably on the record.
 */
export interface WalletKind {
  /** Stable UI identifier for the radio option. */
  key: string;
  /** Human-readable label. */
  label: string;
  /** Short helper line shown under the label. */
  description: string;
  /** Gateway curve string. */
  curve: string;
  /** Gateway chain key, or null to let the gateway infer it from the curve. */
  chain: string | null;
  /**
   * Whether this kind may still be CREATED. Absent means yes.
   *
   * A kind is withdrawn from creation while it cannot be operated safely, but
   * stays in the catalog because wallets of that kind already exist and need a
   * label wherever they are listed. Deleting the entry instead would leave those
   * wallets rendering as an unknown kind — a worse outcome than an option the
   * create form does not offer.
   */
  creatable?: boolean;
}


/**
 * The selectable wallet kinds, in display order. Frozen so a view cannot mutate
 * the shared catalog.
 */
export const WALLET_KINDS: ReadonlyArray<WalletKind> = Object.freeze([
  {
    key: 'evm',
    label: 'EVM account',
    description: 'One address for Ethereum, BSC, Base, and other EVM networks.',
    curve: 'secp256k1',
    chain: 'evm',
  },
  {
    key: 'tron',
    label: 'Tron',
    description: 'A Tron address using the same elliptic curve as EVM.',
    curve: 'secp256k1',
    chain: null,
  },
  {
    key: 'solana',
    label: 'Solana',
    description: 'A Solana Mainnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'solana',
  },
  {
    key: 'solana-testnet',
    label: 'Solana Testnet',
    description: 'A Solana Testnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'solana-testnet',
  },
  {
    key: 'solana-devnet',
    label: 'Solana Devnet',
    description: 'A Solana Devnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'solana-devnet',
  },
  {
    key: 'sui',
    label: 'Sui',
    description: 'A Sui Mainnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'sui',
  },
  {
    key: 'sui-testnet',
    label: 'Sui Testnet',
    description: 'A Sui Testnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'sui-testnet',
  },
  {
    key: 'sui-devnet',
    label: 'Sui Devnet',
    description: 'A Sui Devnet address on the ed25519 curve.',
    curve: 'ed25519',
    chain: 'sui-devnet',
  },
  {
    key: 'bitcoin',
    label: 'Bitcoin',
    description: 'A Bitcoin address, compatible everywhere.',
    // SegWit v0 (P2WPKH), a secp256k1 key. The chain is EXPLICIT and must stay
    // that way: secp256k1's default chain is `evm`, so a null here would quietly
    // provision a second EVM wallet under a Bitcoin label.
    curve: 'secp256k1',
    chain: 'bitcoin',
  },
  {
    key: 'bitcoin-taproot',
    label: 'Bitcoin (Taproot)',
    description: 'A Taproot Bitcoin address. Not offered to new wallets yet.',
    curve: 'schnorr-taproot-secp256k1',
    chain: null,
    // WITHHELD FROM THE CREATE UI. `reshare` refuses Taproot wallets and its
    // gate is open in deployment, so a share on one of these can never be
    // rotated after a compromise — there is no incident response for it. The
    // entry survives because EXISTING Taproot wallets still need a label and a
    // description wherever they are listed; only creation is withdrawn.
    // Restore by deleting this flag once the reshare gate is closed.
    creatable: false,
  },
]);

/**
 * The kinds a user may create: the catalog minus anything withdrawn.
 *
 * Use this for the create form. Use {@link WALLET_KINDS} for LOOKUP — labelling
 * a wallet that already exists — because a user's existing wallet of a withdrawn
 * kind would otherwise render with no name at all, which is worse than an option
 * the create form declines to offer.
 */
export const CREATABLE_WALLET_KINDS: ReadonlyArray<WalletKind> = Object.freeze(
  WALLET_KINDS.filter((k) => k.creatable !== false),
);

/**
 * Wallet kinds visible on one active tier.
 *
 * Solana and Sui keys are concrete networks, so their mainnet and non-mainnet
 * variants are mutually filtered. Solana Testnet and Devnet, and Sui Testnet
 * and Devnet, share the testnet tier and therefore remain simultaneously
 * selectable. Bitcoin has a separate capability flag because serving other
 * mainnet chains does not prove a mainnet Bitcoin node exists.
 */
export function creatableWalletKindsForNetwork(
  activeEnv: 'testnet' | 'mainnet',
  bitcoinMainnetServed: boolean | null | undefined,
): ReadonlyArray<WalletKind> {
  return CREATABLE_WALLET_KINDS.filter((kind) => {
    if (
      kind.key === 'bitcoin' &&
      activeEnv === 'mainnet' &&
      bitcoinMainnetServed !== true
    ) {
      return false;
    }
    const tier = chainKeyTier(kind.chain);
    if (tier === null) return true;
    return tier === (activeEnv === 'testnet');
  });
}

/**
 * Map a gateway balance entry to the store's BalanceLine shape.
 *
 * @param b Gateway BalanceEntry record.
 * @returns The mapped balance line.
 */
export function mapBalanceLine(b: Record<string, unknown>): BalanceLine {
  return {
    symbol: String(b.symbol ?? ''),
    decimals: Number(b.decimals ?? 0),
    raw: String(b.raw ?? '0'),
    amount: String(b.amount ?? '0'),
    usdValue: numOrNull(b.usd_value),
    usdPrice: numOrNull(b.usd_price),
    change24h: numOrNull(b.change_24h),
    verified: Boolean(b.verified),
    error: strOrNull(b.error),
  };
}

/**
 * A synthetic zero native line for unified EVM wallets. A unified EVM wallet has
 * no single native coin (each network carries its own), so the per-network
 * natives live in the tokens array and this placeholder is what the UI hides.
 */
function zeroNativeLine(): BalanceLine {
  return {
    symbol: '',
    decimals: 0,
    raw: '0',
    amount: '0',
    usdValue: null,
    usdPrice: null,
    change24h: null,
    verified: false,
    error: null,
  };
}

/**
 * Whether a mapped balance line carries a non-empty balance worth showing. A
 * line qualifies when its amount is positive or it has a priced USD value, so
 * zero, unpriced dust never adds an empty row to the list.
 */
function lineHasValue(line: BalanceLine): boolean {
  return Number(line.amount) > 0 || line.usdValue != null;
}

/**
 * Concrete EVM chain keys by their numeric chain id, covering both tiers. A
 * deployment that serves both tiers returns every EVM network in one unified
 * response, so each id resolves to a tier-specific key: the mainnet ids
 * (ethereum, bsc, base, hyperevm) and the testnet ids (sepolia, bsc-testnet,
 * base-sepolia, hyperevm-testnet). Naming the tier per line is what lets the
 * per-line tier filter drop an off-tier network, so a testnet-mode session never
 * shows or sums a mainnet EVM line and the reverse. A network whose id is not
 * listed falls back to a name-based derivation, then to null, so an unknown
 * network keeps the generic EVM treatment rather than a wrong concrete chain.
 */
const EVM_CHAIN_KEY_BY_ID: Record<number, string> = {
  1: 'ethereum',
  56: 'bsc',
  8453: 'base',
  999: 'hyperevm',
  11155111: 'sepolia',
  97: 'bsc-testnet',
  84532: 'base-sepolia',
  998: 'hyperevm-testnet',
};

/**
 * Concrete EVM chain keys by a normalized display name, covering both tiers. The
 * gateway's per-network display_name is the fallback when a chain id is absent or
 * unrecognized, matched case-insensitively so "BNB Smart Chain", "Base", and the
 * testnet names ("Sepolia", "BNB Testnet", "Base Sepolia", "HyperEVM Testnet")
 * resolve regardless of the exact casing the gateway sends. The testnet names are
 * included so a testnet line still resolves a tier-specific key when its id is
 * missing, keeping the per-line tier filter accurate on either signal.
 */
const EVM_CHAIN_KEY_BY_NAME: Record<string, string> = {
  ethereum: 'ethereum',
  'bnb smart chain': 'bsc',
  'binance smart chain': 'bsc',
  bsc: 'bsc',
  base: 'base',
  hyperevm: 'hyperevm',
  sepolia: 'sepolia',
  'bnb testnet': 'bsc-testnet',
  'bnb smart chain testnet': 'bsc-testnet',
  'bsc testnet': 'bsc-testnet',
  'base sepolia': 'base-sepolia',
  'hyperevm testnet': 'hyperevm-testnet',
};

/**
 * Derive a concrete EVM chain key from a network entry's chain id or display
 * name. The chain id is the more reliable signal and is tried first; the display
 * name is the fallback. Returns null when neither names a chain we recognize, so
 * the line keeps the generic EVM treatment rather than a guessed chain.
 *
 * @param chainId The network's numeric chain id, or null when absent.
 * @param displayName The network's display name, or an empty string.
 * @returns A concrete chain key, or null when unrecognized.
 */
function evmChainKey(chainId: number | null, displayName: string): string | null {
  if (chainId != null && EVM_CHAIN_KEY_BY_ID[chainId]) {
    return EVM_CHAIN_KEY_BY_ID[chainId];
  }
  const name = displayName.trim().toLowerCase();
  if (name && EVM_CHAIN_KEY_BY_NAME[name]) return EVM_CHAIN_KEY_BY_NAME[name];
  return null;
}

/**
 * Map the gateway's unified EVM balances response to the store's WalletBalances
 * shape. The unified response has no top-level native or tokens; instead it
 * carries a networks array, each entry holding one network's native coin plus
 * its token balances. Every network's native and tokens are flattened into a
 * single tokens list so the one EVM address renders all its per-network assets
 * as rows. The native coin is included as a line, using the network's
 * native_symbol when the native entry itself lacks a symbol. Empty entries are
 * dropped so only assets with a balance or a priced value appear. The native
 * field is a synthetic zero line the UI hides, since a unified EVM wallet has no
 * single native coin.
 *
 * Each line records the concrete network it came from when the entry names one,
 * derived from the network's chain id or display name. That per-line network is
 * what lets a flattened USDC or HYPE line show its real chain (Base, HyperEVM)
 * rather than the generic EVM tag, which the symbol alone cannot recover.
 *
 * @param r Gateway UnifiedEvmBalancesResponse record.
 * @returns The mapped balances view.
 */
function mapUnifiedEvmBalances(r: Record<string, unknown>): WalletBalances {
  const networks = Array.isArray(r.networks) ? r.networks : [];
  const lines: BalanceLine[] = [];

  for (const raw of networks) {
    const net = asRecord(raw);
    const nativeSymbol = net.native_symbol == null ? '' : String(net.native_symbol);

    // The concrete network this entry describes, derived once and stamped on each
    // line the entry contributes. When the entry names no recognized chain the
    // network is left undefined, so those lines keep the generic EVM treatment.
    const displayName = net.display_name == null ? '' : String(net.display_name);
    const chainKey = evmChainKey(numOrNull(net.chain_id), displayName);
    const network: BalanceLineNetwork | undefined = chainKey
      ? { chainKey, displayName: displayName || chainKey }
      : undefined;

    // The network's native coin becomes a line. Fall back to the network's
    // native_symbol when the native entry omits its own symbol.
    const native = asRecord(net.native);
    const nativeLine = mapBalanceLine(native);
    if (nativeLine.symbol === '') nativeLine.symbol = nativeSymbol;
    if (network) nativeLine.network = network;
    // Stamped here because it is the last point the distinction survives: once
    // this network's native coin and its tokens are flattened into the shared
    // list below, nothing about the line says which it was, and the symbol
    // cannot recover it (HYPE is native on HyperEVM and a token elsewhere). The
    // EVM send path builds its transaction client-side and can only encode a
    // native transfer, so it needs this flag to refuse a token cleanly.
    nativeLine.isNative = true;
    if (lineHasValue(nativeLine)) lines.push(nativeLine);

    // Then every token on this network.
    const tokens = Array.isArray(net.tokens) ? net.tokens : [];
    for (const t of tokens) {
      const tokenLine = mapBalanceLine(asRecord(t));
      if (network) tokenLine.network = network;
      tokenLine.isNative = false;
      if (lineHasValue(tokenLine)) lines.push(tokenLine);
    }
  }

  return {
    walletId: String(r.wallet_id),
    // NOT String(): the same coercion hazard fixed in mapWallet. An object from
    // this endpoint became "[object Object]" — a non-empty string that the
    // address surfaces prefer over the summary's, so it reached display, copy
    // and QR. Empty rather than null because WalletBalances.address is a string.
    address: addressOrNull(r.address) ?? '',
    chain: 'evm',
    native: zeroNativeLine(),
    tokens: lines,
    totalUsd: Number(r.total_usd ?? 0),
    pricedAt: strOrNull(r.priced_at),
    partial: Boolean(r.partial),
    pending: Boolean(r.pending),
  };
}

/**
 * Map a gateway WalletBalancesResponse to the store's WalletBalances shape.
 *
 * The gateway returns two shapes. Non-EVM wallets return a flat record with
 * top-level native and tokens. Unified EVM wallets return a networks array with
 * no top-level native or tokens; those are detected by an array-valued networks
 * field and flattened by {@link mapUnifiedEvmBalances}.
 *
 * @param r Gateway balances record.
 * @returns The mapped balances view.
 */
export function mapBalances(r: Record<string, unknown>): WalletBalances {
  if (Array.isArray(r.networks)) return mapUnifiedEvmBalances(r);
  return {
    walletId: String(r.wallet_id),
    // NOT String(): the same coercion hazard fixed in mapWallet. An object from
    // this endpoint became "[object Object]" — a non-empty string that the
    // address surfaces prefer over the summary's, so it reached display, copy
    // and QR. Empty rather than null because WalletBalances.address is a string.
    address: addressOrNull(r.address) ?? '',
    chain: String(r.chain ?? ''),
    native: mapBalanceLine(asRecord(r.native)),
    tokens: Array.isArray(r.tokens) ? r.tokens.map((t) => mapBalanceLine(asRecord(t))) : [],
    totalUsd: Number(r.total_usd ?? 0),
    pricedAt: strOrNull(r.priced_at),
    partial: Boolean(r.partial),
    pending: Boolean(r.pending),
  };
}

/**
 * The assets a view can offer for a wallet, for example the Send asset picker.
 * Returns every balance line the wallet can act on: the native coin first when
 * it is a real coin (unified EVM wallets carry a synthetic empty native that is
 * skipped), then the tokens. Views that already read tokens[] keep working; this
 * gives a single ordered list that treats flat and unified wallets alike.
 *
 * @param balances The mapped balances view, or null before it loads.
 * @returns The selectable balance lines, in display order.
 */
export function assetChoices(balances: WalletBalances | null): BalanceLine[] {
  if (!balances) return [];
  const choices: BalanceLine[] = [];
  // The unified EVM native is a synthetic empty placeholder; skip it. A real
  // native coin (flat wallets) leads the list.
  if (balances.native.symbol !== '') choices.push(balances.native);
  return choices.concat(balances.tokens);
}

/**
 * Map a gateway transaction record to the store's ActivityItem shape.
 *
 * @param t Gateway TransactionResponse record.
 * @returns The mapped activity item.
 */
export function mapActivity(t: Record<string, unknown>): ActivityItem {
  return {
    id: String(t.id),
    walletId: String(t.wallet_id),
    destination: String(t.destination ?? ''),
    amount: String(t.amount ?? '0'),
    asset: String(t.asset ?? ''),
    chain: String(t.chain ?? ''),
    status: String(t.status ?? 'unknown'),
    txHash: strOrNull(t.tx_hash),
    direction: String(t.direction ?? 'send'),
    createdAt: strOrNull(t.created_at),
    confirmedAt: strOrNull(t.confirmed_at),
    confirmations: Number(t.confirmations ?? 0),
    note: strOrNull(t.note),
    counterparty: strOrNull(t.counterparty),
  };
}

/** Options that carry only an abort signal. */
interface SignalOptions {
  /** Abort signal. */
  signal?: AbortSignal;
}

/**
 * Load the wallet list into the store.
 *
 * @param opts Options.
 * @returns The loaded summaries.
 */
export async function loadWallets(opts: SignalOptions = {}): Promise<WalletSummary[]> {
  setWalletsLoading(true);
  try {
    const records = await apiGetWallets({ signal: opts.signal });
    const list = Array.isArray(records) ? records.map(mapWallet) : [];
    setWallets(list);
    return list;
  } catch (err) {
    // A caller-cancelled read (unmount or effect re-run) is not an error to show.
    if (err instanceof ApiError && err.aborted) throw err;
    setWalletsError(messageFor(err));
    throw err;
  }
}

/** Fields accepted when creating a wallet. */
export interface CreateWalletInput {
  /** Non-blank wallet label. */
  label: string;
  /** Signing threshold t. */
  threshold: number;
  /** Total parties n. */
  numParties: number;
  /** Gateway curve string from a {@link WalletKind}. */
  curve: string;
  /** Gateway chain key, or null to let the gateway infer it from the curve. */
  chain?: string | null;
}

/**
 * Create a wallet and place it into the store on success.
 *
 * Validation mirrors the gateway's own bounds so an out-of-range request fails
 * before the network rather than after a wasted round trip. The gateway remains
 * the authority and re-checks every bound; this is a fast local guard, not the
 * trust boundary. On success the new record is mapped to a WalletSummary and
 * upserted to the front of the list so the dashboard shows it without a refetch.
 *
 * @param input Create input.
 * @param opts Options.
 * @returns The created wallet summary.
 * @throws {Error} On invalid input, before any network call.
 * @throws {ApiError} On a gateway rejection, for example a duplicate label.
 */
export async function createWallet(
  input: CreateWalletInput,
  opts: SignalOptions = {},
): Promise<WalletSummary> {
  const label = String(input.label ?? '').trim();
  const threshold = Number(input.threshold);
  const numParties = Number(input.numParties);
  const curve = String(input.curve ?? '').trim();

  // Mirror the gateway's bounds (1 <= t <= n <= 8) so a bad request never
  // spends a DKG ceremony. Whole-number checks run before the range checks so a
  // fractional value reports the clearer message.
  if (label === '') throw new Error('Enter a wallet label.');
  if (curve === '') throw new Error('Choose a wallet type.');
  if (!Number.isInteger(threshold) || !Number.isInteger(numParties)) {
    throw new Error('Threshold and parties must be whole numbers.');
  }
  if (numParties < 1 || numParties > 8) {
    throw new Error('The number of parties must be between 1 and 8.');
  }
  if (threshold < 1 || threshold > numParties) {
    throw new Error('Threshold must be between 1 and the number of parties.');
  }

  const body: Record<string, unknown> = {
    label,
    threshold,
    num_parties: numParties,
    curve,
  };
  // Only carry chain when the caller pins one. A null chain is omitted so the
  // gateway resolves the concrete per-tier chain from the curve, which is safe
  // only for Bitcoin (its curve is unique). Tron passes a concrete key chosen by
  // the create view, since its curve is shared with EVM and a null chain would
  // resolve to EVM.
  if (input.chain != null) body.chain = input.chain;

  const record = await apiCreateWallet(body, { signal: opts.signal });
  const wallet = mapWallet(record);
  upsertWallet(wallet);
  return wallet;
}

/** Options for a balances read. */
interface BalancesOptions {
  /** Optional chain override. */
  chain?: string;
  /**
   * Imported custom-token contract addresses to refresh alongside the curated
   * balances, so an imported token's balance updates on every poll. Forwarded to
   * the gateway, which appends each as an extra unverified entry with a null fiat
   * and caps the number it processes. Omitted or empty means an ordinary read.
   */
  imports?: readonly string[];
  /** Abort signal. */
  signal?: AbortSignal;
}

/**
 * Load one wallet's balances into the store. Always network-only.
 *
 * @param walletId Wallet uuid.
 * @param opts Options.
 * @returns The loaded balances view.
 */
export async function loadBalances(
  walletId: string,
  opts: BalancesOptions = {},
): Promise<WalletBalances> {
  setBalancesLoading(true);
  try {
    const record = await apiGetWalletBalances(walletId, opts);
    const balances = mapBalances(record);
    setWalletBalances(walletId, balances);
    return balances;
  } catch (err) {
    // A caller-cancelled read is not an error to show; a later pass re-runs it.
    if (err instanceof ApiError && err.aborted) throw err;
    setBalancesError(messageFor(err));
    throw err;
  }
}

/** Options for an activity history read. */
interface ActivityOptions {
  /** Restrict to one wallet. */
  walletId?: string;
  /** Page size. */
  limit?: number;
  /** Abort signal. */
  signal?: AbortSignal;
}

/**
 * Load the first page of activity history into the store, replacing any prior
 * page.
 *
 * @param opts Options.
 * @returns The first page of items.
 */
export async function loadActivity(opts: ActivityOptions = {}): Promise<ActivityItem[]> {
  setActivityLoading(true);
  try {
    const page = await apiGetTransactions(opts);
    const items = pageRows(page).map(mapActivity);
    setActivity(items, page?.next_cursor ?? null);
    return items;
  } catch (err) {
    setActivityError(messageFor(err));
    throw err;
  }
}

/**
 * Read the transaction rows out of a gateway TxPage. The gateway serializes the
 * rows under `items`; an older `transactions` field is tolerated as a fallback so
 * a mixed-version deploy never blanks the feed. A missing or non-array page
 * yields an empty list rather than throwing.
 *
 * @param page The page returned by the transactions endpoint.
 * @returns The row records, or an empty array when the page carries none.
 */
function pageRows(
  page: { items?: unknown; transactions?: unknown } | null | undefined,
): Record<string, unknown>[] {
  if (Array.isArray(page?.items)) return page.items as Record<string, unknown>[];
  if (Array.isArray(page?.transactions)) return page.transactions as Record<string, unknown>[];
  return [];
}

/**
 * Load and append the next page of activity history using a cursor.
 *
 * @param cursor Keyset cursor from the previous page.
 * @param opts Options.
 * @returns The appended items.
 */
export async function loadMoreActivity(
  cursor: string,
  opts: ActivityOptions = {},
): Promise<ActivityItem[]> {
  setActivityLoading(true);
  try {
    const page = await apiGetTransactions({ ...opts, cursor });
    const items = pageRows(page).map(mapActivity);
    appendActivity(items, page?.next_cursor ?? null);
    return items;
  } catch (err) {
    setActivityError(messageFor(err));
    throw err;
  }
}

/**
 * A resolved imported custom token, as returned by the gateway resolve endpoint
 * and shown in the import preview. It carries the stored import fields plus the
 * live balance the preview displays, so the sheet can confirm the wallet actually
 * holds the token before the user commits the import. The verified flag is always
 * false and the fiat is always null from the gateway, so an imported token can
 * never badge as curated or inflate a total.
 */
export interface ResolvedImportedToken {
  /** The stored import record, ready to hand to addImportedToken. */
  token: ImportedToken;
  /** The wallet's smallest-unit balance of the token, as a base-10 string. */
  balanceRaw: string;
  /** The wallet's human-readable decimal balance of the token, as a string. */
  balanceAmount: string;
}

/** Fields needed to resolve an imported token against a wallet. */
export interface ResolveImportedTokenInput {
  /** The chain family key, for example "evm", "tron", "solana", or "sui". */
  chain: string;
  /** The concrete network key for a multi-network wallet, or undefined. */
  network?: string;
  /** The token contract address to resolve. */
  contract: string;
}

/**
 * Resolve an imported custom token's metadata and balance for one wallet, mapping
 * the gateway's snake_case record onto the camelCase shapes the import preview
 * reads. The gateway derives the owner address server-side and returns the token
 * verified false with a null fiat, so this never trusts a client-supplied address
 * or price. The returned token carries the network only when the caller pinned one
 * (a multi-network wallet), so a flat wallet's import stays untagged.
 *
 * @param walletId Wallet uuid the token is resolved against.
 * @param input The chain, optional network, and contract to resolve.
 * @param opts Options.
 * @returns The resolved token plus its live balance.
 * @throws {ApiError} On a gateway rejection, for example a non-token address.
 */
export async function resolveImportedToken(
  walletId: string,
  input: ResolveImportedTokenInput,
  opts: SignalOptions = {},
): Promise<ResolvedImportedToken> {
  const record = await apiResolveImportedToken(
    walletId,
    {
      chain: input.chain,
      network: input.network,
      contract_address: input.contract,
    },
    { signal: opts.signal },
  );

  // The gateway echoes the contract it resolved; fall back to the requested one so
  // the stored identity is stable even if the echo is absent.
  const contract = String(record.contract_address ?? input.contract);
  const token: ImportedToken = {
    chain: input.chain,
    contract,
    symbol: String(record.symbol ?? ''),
    name: String(record.name ?? ''),
    decimals: Number(record.decimals ?? 0),
  };
  // Pin the network only when the caller supplied one, so a flat wallet import is
  // not stamped with a concrete network it does not have.
  if (input.network) token.network = input.network;

  return {
    token,
    balanceRaw: String(record.balance_raw ?? '0'),
    balanceAmount: String(record.balance_amount ?? '0'),
  };
}

/**
 * Turn any thrown value into a short user-facing message. Exported so views that
 * call the write helpers in this service surface failures the same way the load
 * helpers do.
 *
 * @param err The thrown value.
 * @returns A concise message.
 */
export function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.offline) return 'You appear to be offline.';
    if (err.timeout) return 'The request timed out. Try again.';
    // A 401 only means the session is over when the refresh flow gave up and
    // cleared it, which also wipes the persisted refresh token. While that
    // token is still present the session survived — the 401 was a transient
    // failure on the auth path (a gateway restart, a refresh call that timed
    // out once) — so the copy must invite a retry, not claim an expiry a
    // reload would disprove.
    if (err.status === 401) {
      return getRefreshToken()
        ? 'Could not verify your session. Try again.'
        : 'Your session has expired.';
    }
    // A 403 is the gateway refusing this action for an authenticated caller.
    // Re-login cannot fix it, so it must not wear the session-expired wording.
    if (err.status === 403) return 'You do not have permission to do this.';
    return err.message || 'The request failed.';
  }
  return err instanceof Error ? err.message : 'The request failed.';
}
